import React, { useState, useEffect, useRef } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';

export function SearchableSelect({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  className = "",
  dataTestId = ""
}) {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState(0);
  const prevOptionsLength = useRef(options.length);

  // Ensure options is always an array
  const safeOptions = Array.isArray(options) ? options : [];

  // Force re-render when options change (fixes dynamic data loading issue)
  useEffect(() => {
    if (safeOptions.length !== prevOptionsLength.current) {
      console.log(`[SearchableSelect] Options updated: ${prevOptionsLength.current} → ${safeOptions.length}`);
      prevOptionsLength.current = safeOptions.length;
      // Force Command component to re-render with new options
      setKey(prev => prev + 1);
    }
  }, [safeOptions.length]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between input-field ${className}`}
          data-testid={dataTestId}
        >
          <span className={value ? "text-slate-900" : "text-slate-500"}>
            {value || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" style={{ width: 'var(--radix-popover-trigger-width)' }}>
        <Command key={key} shouldFilter={true}>
          <CommandInput 
            placeholder={searchPlaceholder}
          />
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup className="max-h-64 overflow-y-auto">
            {safeOptions.map((option) => (
              <CommandItem
                key={option}
                value={option}
                onSelect={(currentValue) => {
                  onChange(currentValue);
                  setOpen(false);
                }}
              >
                <Check
                  className={`mr-2 h-4 w-4 ${
                    value === option ? "opacity-100" : "opacity-0"
                  }`}
                />
                {option}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
